import {
  getRowItemsFromSectionItem,
  isPkceEnabled,
} from "@superblocksteam/shared";
import type { FormTemplate } from "@superblocksteam/shared";

const VERIFIER_BYTES = 32;

const toBase64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");

export const generatePkceVerifier = (): string =>
  toBase64Url(crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));

export const derivePkceChallenge = async (
  verifier: string,
): Promise<string> => {
  if (!crypto.subtle) {
    throw new Error("PKCE requires Web Crypto in a secure browser context");
  }

  return toBase64Url(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)),
    ),
  );
};

export const addPkceChallengeToUrl = async (
  authorizationUrl: URL,
  verifier: string,
): Promise<void> => {
  authorizationUrl.searchParams.set(
    "code_challenge",
    await derivePkceChallenge(verifier),
  );
  authorizationUrl.searchParams.set("code_challenge_method", "S256");
};

export const hasPkceClientSecret = (clientSecret: unknown): boolean =>
  typeof clientSecret === "string" && clientSecret.length > 0;

export const setPkceClientSecretUsage = (
  configuration: Record<string, unknown>,
  formTemplate?: FormTemplate,
): void => {
  const authConfig = configuration.authConfig;
  if (
    typeof authConfig !== "object" ||
    authConfig === null ||
    Array.isArray(authConfig)
  ) {
    return;
  }

  if (!("usePkce" in authConfig) || !isPkceEnabled(authConfig.usePkce)) {
    Reflect.deleteProperty(authConfig, "usePkceClientSecret");
    return;
  }

  const canConfigureClientSecret =
    !formTemplate ||
    formTemplate.sections.some((section) =>
      section.items
        .flatMap(getRowItemsFromSectionItem)
        .some(
          (item) =>
            item.name === "authConfig.clientSecret" &&
            !item.hidden &&
            item.display?.show?.["authConfig.usePkce"]?.some(isPkceEnabled),
        ),
    );
  if (!canConfigureClientSecret) return;

  Object.assign(authConfig, {
    usePkceClientSecret: hasPkceClientSecret(
      "clientSecret" in authConfig ? authConfig.clientSecret : undefined,
    ),
  });
};
